export type FortuneSaveOS = 'ios' | 'android' | 'desktop';
export type FortuneSaveEngine = 'webkit' | 'blink' | 'gecko' | 'unknown';
export type FortuneSaveBrowser = 'kakao' | 'webview' | 'safari' | 'chromium' | 'firefox' | 'other';

export interface FortuneSavePolicy {
  readonly os: FortuneSaveOS;
  readonly engine: FortuneSaveEngine;
  readonly browser: FortuneSaveBrowser;
  readonly preview: boolean;
  readonly preferNativeShare: boolean;
  readonly download: 'data-url' | 'blob-url' | 'http';
}

/** Host-app restrictions take precedence over browser/engine UA tokens. */
export function fortuneSavePolicy(userAgent: string, maxTouchPoints = 0): FortuneSavePolicy {
  const os: FortuneSaveOS = /iPhone|iPad|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)
    ? 'ios' : /Android/i.test(userAgent) ? 'android' : 'desktop';
  const kakao = /KAKAOTALK/i.test(userAgent);
  const webview = /;\s*wv\)|\bNAVER\b|Instagram|FBAN|FBAV|Line\//i.test(userAgent)
    || (os === 'android' && /Version\/4\.0.*Chrome\//i.test(userAgent));
  const chromium = /Chrome\/|Chromium\/|CriOS\/|Edg(?:A|iOS)?\/|SamsungBrowser\//i.test(userAgent);
  const firefox = /Firefox\/|FxiOS\//i.test(userAgent);
  const browser: FortuneSaveBrowser = kakao ? 'kakao' : webview ? 'webview'
    : firefox ? 'firefox' : chromium ? 'chromium' : /Safari\//i.test(userAgent) ? 'safari' : 'other';
  // CriOS/FxiOS still have WebKit-shaped iOS UAs. Capabilities are checked on
  // the actual File separately; an engine label never grants share support.
  const engine: FortuneSaveEngine = os === 'ios' ? 'webkit'
    : chromium ? 'blink' : firefox ? 'gecko' : /AppleWebKit\//i.test(userAgent) ? 'webkit' : 'unknown';
  const requiresHttp = (os === 'android' && (kakao || webview)) || (kakao && os === 'desktop');
  return {
    os, engine, browser,
    preview: os !== 'desktop' || requiresHttp,
    preferNativeShare: os === 'ios',
    // Kakao: data URLs are iOS-only. Android's native download manager needs
    // a real HTTP response, not a renderer-local blob or data URL.
    // https://devtalk.kakao.com/t/topic/146168
    download: requiresHttp ? 'http' : os === 'ios' ? 'data-url' : 'blob-url',
  };
}
